import React, { useMemo } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { DIALOG_ACTIONS, UIConfirmDialog } from '@futrli/ui-components/components/ui-confirm-dialog/ui-confirm-dialog';

import styles from './confirm-template-action-modal.scss';

interface Props {
  onClose: () => void;
  onConfirm: () => void;
  isModalOpen: boolean;
  action: 'publish' | 'unpublish' | 'delete' | 'edit';
  ownerName?: string;
  isDraft?: boolean;
}

export const ConfirmTemplateActionModal = ({ onConfirm, onClose, isModalOpen, action, ownerName, isDraft }: Props) => {
  const intl = useIntl();

  const modalContent = useMemo(
    () => ({
      publish: [
        intl.formatMessage({
          id: 'reporting.confirm_no_sensitive_info',
          defaultMessage:
            'Please ensure that there is no sensitive information within Text Widgets prior to publishing this to your workspace.'
        }),
        intl.formatMessage({
          id: 'reporting.guest_has_no_access',
          defaultMessage: 'Guest users will not have access to any workspace templates.'
        }),
        intl.formatMessage({
          id: 'reporting.reports_may_be_created_in_any_org',
          defaultMessage:
            'Reports may be created from this template in any of the organisations non-guest users have access to.'
        }),
        intl.formatMessage({
          id: 'reporting.workspace_templates_may_be_unpublished',
          defaultMessage: 'Workspace templates may be unpublished and set to a draft state at any time.'
        })
      ],
      unpublish: [
        intl.formatMessage({
          id: 'reporting.unpublish_template_remove_from_workspaces',
          defaultMessage: 'Unpublishing this template will remove it from all non-guest workspaces.'
        }),
        <>
          <FormattedMessage
            id="reporting.already_created_reports_unaffected"
            defaultMessage="Any report previously created from this template <b>will be unaffected</b>."
            values={{
              b: chunks => <b>{chunks}</b>
            }}
          />
        </>
      ],
      delete: [
        intl.formatMessage({
          id: 'reporting.deleting_template_permanent',
          defaultMessage: 'Deleting this template will remove it permanently from all non-guest workspaces.'
        }),
        intl.formatMessage({
          id: 'reporting.this_cannot_be_undone',
          defaultMessage: 'This cannot be undone.'
        }),
        <>
          <FormattedMessage
            id="reporting.already_created_reports_unaffected"
            defaultMessage="Any report previously created from this template <b>will be unaffected</b>."
            values={{
              b: chunks => <b>{chunks}</b>
            }}
          />
        </>
      ],
      edit: [
        <>
          <FormattedMessage
            id="reporting.edits_will_be_reflected_in_new_reports"
            defaultMessage="All edits will be reflected immediately in every <b>new</b> report created from it."
            values={{
              b: chunks => <b>{chunks}</b>
            }}
          />
        </>,
        <>
          <FormattedMessage
            id="reporting.already_created_reports_unaffected"
            defaultMessage="Any report previously created from this template <b>will be unaffected</b>."
            values={{
              b: chunks => <b>{chunks}</b>
            }}
          />
        </>,
        intl.formatMessage({
          id: 'reporting.for_safe_editing_unpublish',
          defaultMessage:
            'For safe editing, please unpublish this report, edit in a draft state, and republish when happy.'
        })
      ]
    }),
    [intl]
  );

  const submitButtonText = useMemo(
    () => ({
      publish: intl.formatMessage({
        id: 'reporting.publish_this_template',
        defaultMessage: 'Publish this template'
      }),
      unpublish: intl.formatMessage({
        id: 'reporting.unpublish_this_template',
        defaultMessage: 'Unpublish this template'
      }),
      delete: intl.formatMessage({
        id: 'reporting.delete_this_template',
        defaultMessage: 'Delete this template'
      }),
      edit: intl.formatMessage({
        id: 'reporting.edit_this_template',
        defaultMessage: 'Edit this template'
      })
    }),
    [intl]
  );
  const translatedActionNames = useMemo(
    () => ({
      publish: intl.formatMessage({
        id: 'reporting.publishing_workspace_template',
        defaultMessage: 'Publishing a workspace template'
      }),
      unpublish: intl.formatMessage({
        id: 'reporting.unpublishing_workspace_template',
        defaultMessage: 'Unpublishing a workspace template'
      }),
      delete: intl.formatMessage({
        id: 'reporting.deleting_workspace_template',
        defaultMessage: 'Deleting a workspace template'
      }),
      edit: intl.formatMessage({
        id: 'reporting.editing_workspace_template',
        defaultMessage: 'Editing a workspace template'
      })
    }),
    [intl]
  );

  return (
    <UIConfirmDialog
      action={action === 'delete' ? DIALOG_ACTIONS.DELETE : DIALOG_ACTIONS.SUBMIT}
      title={
        <>
          {translatedActionNames[action]}{' '}
          {ownerName && (
            <em style={{ fontStyle: 'normal', fontSize: 12, color: '#212121' }}>
              {intl.formatMessage(
                {
                  id: 'reporting.created_by_name',
                  defaultMessage: 'Created by: {name}'
                },
                { name: ownerName }
              )}
            </em>
          )}
        </>
      }
      description={
        <div className={clsx(styles.dialogContent)}>
          <div className={clsx(styles.dialogWarning)}>
            <h2>
              {intl.formatMessage({
                id: 'common.warning_column',
                defaultMessage: 'warning:'
              })}
            </h2>
            {(action === 'unpublish' || action === 'delete' || action === 'edit') && (
              <p>
                {isDraft
                  ? intl.formatMessage({
                      id: 'reporting.draft_workspace_template',
                      defaultMessage: 'This is a draft workspace template'
                    })
                  : intl.formatMessage({
                      id: 'reporting.public_workspace_template',
                      defaultMessage: 'This is a published workspace template'
                    })}
              </p>
            )}
          </div>
          <ol className={clsx(styles.orderedList)}>
            {modalContent[action].map((closure, i) => (
              <li key={i}>{closure}</li>
            ))}
          </ol>
        </div>
      }
      submitButtonText={`${intl.formatMessage({
        id: 'reporting.i_understand',
        defaultMessage: 'I understand'
      })}. ${submitButtonText[action]}`}
      onClose={onClose}
      onSubmit={onConfirm}
      isOpen={isModalOpen}
    />
  );
};
